import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Meetings from "./pages/Meetings";

import "./index.css";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import Bots from "./pages/Bots";
import BotEdit from "./pages/BotEdit";
import BotEmbed from "./pages/BotEmbed";
import Leads from "./pages/Leads";
import Conversations from "./pages/Conversations";
import Analytics from "./pages/Analytics";
import Pricing from "./pages/Pricing";
import Settings from "./pages/Settings";
import AppLayout from "./components/layout/AppLayout";
import WispyFAQ from "./pages/WispyFAQ";
import WispyBlogPost2 from "./pages/WispyBlogPost2";
import WispyBlogPost3 from "./pages/WispyBlogPost3";
import BlogIndex from "./pages/BlogIndex";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="loading-page">
        <div className="spinner" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="loading-page">
        <div className="spinner" />
      </div>
    );
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: "#1c2420",
              color: "#f0f4f1",
              border: "1px solid rgba(255,255,255,0.1)",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "14px",
            },
            success: { iconTheme: { primary: "#1a9e5c", secondary: "#fff" } },
          }}
        />
        <Routes>
          {/* Landing */}
          <Route path="/" element={<Landing />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route
            path="/blog/how-to-add-chatbot-to-website"
            element={<WispyBlogPost2 />}
          />
          <Route
            path="/blog/ai-chatbot-small-business"
            element={<WispyBlogPost3 />}
          />

          <Route path="/faq" element={<WispyFAQ />} />

          {/* Public auth */}
          <Route
            path="/login"
            element={
              <PublicOnly>
                <Login />
              </PublicOnly>
            }
          />
          <Route
            path="/register"
            element={
              <PublicOnly>
                <Register />
              </PublicOnly>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicOnly>
                <ForgotPassword />
              </PublicOnly>
            }
          />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route
            path="/verify-email"
            element={
              <Protected>
                <VerifyEmail />
              </Protected>
            }
          />

          {/* Protected app */}
          <Route
            path="/"
            element={
              <Protected>
                <AppLayout />
              </Protected>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="bots" element={<Bots />} />
            <Route path="bots/:id/edit" element={<BotEdit />} />
            <Route path="bots/:id/embed" element={<BotEmbed />} />
            <Route path="leads" element={<Leads />} />
            <Route path="conversations" element={<Conversations />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="meetings" element={<Meetings />} />

            <Route path="pricing" element={<Pricing />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
